import type {
  ConnectorStyle,
  LayoutType,
  MindmapNode,
  NodeDetail as BaseNodeDetail,
  ViewportPosition,
} from '@blocksuite/blocks';
import { isNodeType, MindmapElementModel } from '@blocksuite/blocks';
import {
  Bound,
  DisposableGroup,
  getCommonBound,
} from '@blocksuite/global/utils';
import { DocCollection, type Y } from '@blocksuite/store';
import { setupAgendaListeners } from '@pique/core/blocksuite/presets/ai/utils/edgeless';
import { posthog } from '@pique/core/posthog';

import {
  highlightLinkedBlocksSlot,
  seekTranscriptionAtTimestampSlot,
} from './pique-slots';

const ZOOM_PADDING: [number, number, number, number] = [20, 50, 50, 50];

export interface PiqueNodeElementExtensions {
  timestamp?: {
    start?: number;
    end?: number;
  };
}

type ExtendedNodeDetail = BaseNodeDetail & {
  category?: string | null;
  timestamp?: string | null;
  linkedBlockTimestamps?: string[] | null;
};

function isValidMindmapNode(node: unknown): node is MindmapNode {
  return (
    !!node &&
    typeof node === 'object' &&
    'element' in node &&
    !!node.element &&
    typeof node.element === 'object' &&
    'id' in node.element
  );
}

export class PiqueMindmapElementModel extends MindmapElementModel {
  static override propsToY(props: Record<string, unknown>) {
    if (
      props['children'] &&
      !(props['children'] instanceof DocCollection.Y.Map) &&
      !isNodeType(props['children'] as Record<string, unknown>)
    ) {
      const children = new DocCollection.Y.Map();
      const keys = Object.keys(props['children'] as Record<string, unknown>);

      keys.forEach(key => {
        const nodeData = (props['children'] as Record<string, unknown>)[key] as
          | Record<string, unknown>
          | undefined;
        if (nodeData && typeof nodeData === 'object') {
          const detail: Record<string, unknown> = {};

          if ('index' in nodeData) detail['index'] = nodeData['index'];
          if ('parent' in nodeData) detail['parent'] = nodeData['parent'];
          if ('preferredDir' in nodeData)
            detail['preferredDir'] = nodeData['preferredDir'];

          if ('category' in nodeData) detail['category'] = nodeData['category'];
          if ('timestamp' in nodeData)
            detail['timestamp'] = nodeData['timestamp'];
          if ('linkedBlockTimestamps' in nodeData)
            detail['linkedBlockTimestamps'] = nodeData['linkedBlockTimestamps'];

          children.set(key, detail);
        }
      });

      props['children'] = children;
    }

    return super.propsToY(props);
  }

  private readonly disposables = new DisposableGroup();

  setNodeMetadata(
    nodeId: string,
    metadata: { category?: string | null; timestamp?: string | null }
  ): void {
    if (!this.surface || !this.tree) return;

    this.surface.doc.transact(() => {
      const map = this.children as Y.Map<ExtendedNodeDetail>;
      const currentDetail = map.get(nodeId);
      const nextDetail = {
        ...currentDetail,
        category: metadata.category ?? null,
        timestamp: metadata.timestamp ?? null,
      } as ExtendedNodeDetail;
      map.set(nodeId, nextDetail);
    });
  }

  getNodeMetadata(nodeId: string): {
    category?: string | null;
    timestamp?: string | null;
  } {
    const map = this.children as Y.Map<ExtendedNodeDetail>;
    const nodeDetail =
      map.get(nodeId) ??
      (this.getNode(nodeId)?.detail as ExtendedNodeDetail | undefined) ??
      undefined;
    return {
      category: nodeDetail?.category ?? null,
      timestamp: nodeDetail?.timestamp ?? null,
    };
  }

  setNodeLinkedTimestamps(nodeId: string, timestamps: string[] | null): void {
    if (!this.surface || !this.tree) return;

    this.surface.doc.transact(() => {
      const map = this.children as Y.Map<ExtendedNodeDetail>;
      const currentDetail = map.get(nodeId);
      const nextDetail = {
        ...currentDetail,
        linkedBlockTimestamps: timestamps,
      } as ExtendedNodeDetail;
      map.set(nodeId, nextDetail);
    });
  }

  getNodeLinkedTimestamps(nodeId: string): string[] | null {
    const map = this.children as Y.Map<ExtendedNodeDetail>;
    const nodeDetail =
      map.get(nodeId) ??
      (this.getNode(nodeId)?.detail as ExtendedNodeDetail | undefined) ??
      undefined;
    return nodeDetail?.linkedBlockTimestamps ?? null;
  }

  override onCreated(): void {
    super.onCreated?.();

    setupAgendaListeners(this, this.disposables);
  }

  // User clicks on the collapse buttons pass layout: true; programmatic calls
  // (setDetailLevel, durable rebuilds) pass layout: false.
  override toggleCollapse(
    node: MindmapNode,
    options: { layout?: boolean } = {}
  ): void {
    if (options.layout && node.children.length > 0) {
      posthog.capture('mindmap_node_expanded', {
        expanded: Boolean(node.detail.collapsed),
      });
    }
    super.toggleCollapse(node, options);
  }

  /**
   * Collapse/expand nodes to show the requested detail level.
   *
   * Level 0 collapses at the root (hiding all children).
   * Level 1 collapses all first-level branches, showing only the root and its direct children, etc.
   * If the requested level is deeper than the tree, this is a no-op.
   */
  setDetailLevel(level: number): void {
    if (!this.tree || !Number.isFinite(level)) return;

    const targetLevel = Math.max(0, Math.floor(level));
    const nodes: Array<{ node: MindmapNode; depth: number }> = [];
    let maxDepth = 0;

    const walk = (node: MindmapNode, depth: number) => {
      nodes.push({ node, depth });
      maxDepth = Math.max(maxDepth, depth);
      node.children.forEach(child => walk(child, depth + 1));
    };

    walk(this.tree, 0);

    if (nodes.length === 0) {
      return;
    }

    if (targetLevel > maxDepth) {
      queueMicrotask(() =>
        this._zoomToTree(this.tree, {
          padding: ZOOM_PADDING,
          stopOnCollapse: true,
          visibleOnly: true,
        })
      );
      return;
    }

    let changed = false;

    nodes.forEach(({ node, depth }) => {
      const hasChildren = node.children.length > 0;
      const shouldCollapse = hasChildren && depth >= targetLevel;
      const currentlyCollapsed = !!node.detail.collapsed;

      if (shouldCollapse !== currentlyCollapsed) {
        // toggleCollapse flips the current state, so call only when a change is needed.
        this.toggleCollapse(node, { layout: false });
        changed = true;
      } else if (shouldCollapse) {
        // Ensure descendants stay hidden if a previously collapsed branch was expanded elsewhere.
        node.children.forEach(child => {
          child.element.display = false;
        });
      }
    });

    if (changed) {
      this.requestLayout();
    }

    queueMicrotask(() =>
      this._zoomToTree(this.tree, {
        padding: ZOOM_PADDING,
        stopOnCollapse: true,
        visibleOnly: true,
      })
    );
  }

  cleanupListeners(): void {
    this.disposables.dispose();
  }

  protected override addConnector(
    from: MindmapNode,
    to: MindmapNode,
    layout: LayoutType,
    style: ConnectorStyle,
    extra = false
  ) {
    const connector = super.addConnector(from, to, layout, style, extra);

    if (to?.element && 'opacity' in to.element) {
      connector.opacity = to.element.opacity;
    }

    return connector;
  }

  handleMindmapNodeClick(nodeId: string): void {
    try {
      const metadata = this.getNodeMetadata(nodeId);
      const label = metadata.timestamp ?? undefined;
      if (label && label.length > 0) {
        seekTranscriptionAtTimestampSlot.emit(label);
      }

      const linkedTimestamps = this.getNodeLinkedTimestamps(nodeId);
      if (linkedTimestamps && linkedTimestamps.length > 0) {
        highlightLinkedBlocksSlot.emit({
          timestamps: linkedTimestamps,
          mindmapNodeId: nodeId,
        });
      }
    } catch {
      // ignore
    }
  }

  zoomToVisible(): void {
    this._zoomToTree(this.tree, {
      padding: ZOOM_PADDING,
      stopOnCollapse: true,
      visibleOnly: true,
    });
  }

  private _zoomToTree(
    root: MindmapNode | null | undefined,
    options?: {
      maxDepth?: number;
      padding?: [number, number, number, number];
      position?: ViewportPosition;
      stopOnCollapse?: boolean;
      visibleOnly?: boolean;
    }
  ): void {
    try {
      if (!isValidMindmapNode(root)) return;
      const maxDepth = options?.maxDepth;
      const nodes: MindmapNode[] = [];
      const stopOnCollapse = options?.stopOnCollapse ?? false;
      const visibleOnly = options?.visibleOnly ?? false;
      const walk = (node: MindmapNode, level: number) => {
        if (!isValidMindmapNode(node)) return;
        if (typeof maxDepth === 'number' && level > maxDepth) return;

        const isHidden =
          visibleOnly && node !== root && node.element.display === false;

        if (!isHidden) {
          nodes.push(node);
        }

        if (stopOnCollapse && node.detail.collapsed) {
          return;
        }

        if (isHidden) {
          return;
        }

        node.children?.forEach(child => walk(child, level + 1));
      };
      walk(root, 0);
      if (nodes.length === 0) return;

      const bounds = nodes
        .filter(isValidMindmapNode)
        .map(
          node =>
            new Bound(
              node.element.x,
              node.element.y,
              node.element.w,
              node.element.h
            )
        );
      const commonBound = getCommonBound(bounds);
      if (!commonBound) return;

      const edgelessRoot = document.querySelector('affine-edgeless-root');
      if (!edgelessRoot) return;
      const edgelessService = (edgelessRoot as any).host?.std?.spec?.getService(
        'affine:page'
      );
      if (!edgelessService?.viewport) return;

      if (
        options?.position &&
        edgelessService.viewport.setViewportByBoundWithPosition
      ) {
        const padding = options.padding ?? ZOOM_PADDING;
        const topPadding = padding[0] ?? 20;
        const horizontalPadding = padding[1] ?? 50;
        edgelessService.viewport.setViewportByBoundWithPosition(
          commonBound,
          options.position,
          horizontalPadding,
          topPadding,
          true
        );
      } else {
        edgelessService.viewport.setViewportByBound(
          commonBound,
          options?.padding ?? ZOOM_PADDING,
          true
        );
      }
    } catch {
      // ignore
    }
  }
}
